import {
  ACCESSIBILITY_BUTTON_ID,
  ACCESSIBILITY_BUTTON_NATIVE_SIZE_PX,
  ACCESSIBILITY_DRAG_THRESHOLD_PX,
  ACCESSIBILITY_EDGE_MARGIN_PX,
  ACCESSIBILITY_FRAME_INSET_DESKTOP,
  ACCESSIBILITY_FRAME_INSET_MOBILE,
  ACCESSIBILITY_FRAME_SELECTORS,
  ACCESSIBILITY_ICON_SIZE_PX,
  ACCESSIBILITY_ICON_Z_INDEX,
  ACCESSIBILITY_SHADOW_HOST_ID,
} from '@/constants/accessibility';
import { isMobileDevice } from '@/utils/device';

/**
 * Where the icon sits, as a fraction of the travel it has on each axis
 * **inside the checkout card**: `{ x: 0, y: 1 }` is the card's bottom-left,
 * `{ x: 1, y: 0 }` its top-right. A ratio rather than pixels so a rotation,
 * a breakpoint change or a keyboard opening re-places the icon
 * proportionally instead of leaving it stranded.
 */
export interface AccessibilityPositionRatio {
  x: number;
  y: number;
}

/**
 * The box the icon lives in — the checkout card, in viewport coordinates.
 * Everything is measured against this rather than the window, because above
 * `md` the card is a centred 976x871 box: a window corner would put the icon
 * out on the scrim, outside the frame the design draws it in.
 */
export interface IconFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Holds a position ratio inside its only meaningful range. 0 is the icon
 * pinned to the start of the frame's travel on that axis, 1 the end; anything
 * outside would place the icon off the card.
 */
const clampRatio = (ratio: number): number => Math.min(1, Math.max(0, ratio));

/** The whole window, used while the card has not mounted yet (boot skeleton). */
const viewportFrame = (): IconFrame => ({
  left: 0,
  top: 0,
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * First mounted checkout card from {@link ACCESSIBILITY_FRAME_SELECTORS}
 * (modal content, then preferred bottom-sheet panel).
 */
export const findAccessibilityFrameElement = (): HTMLElement | null => {
  for (const selector of ACCESSIBILITY_FRAME_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return null;
};

export const currentFrame = (): IconFrame => {
  const card = findAccessibilityFrameElement()?.getBoundingClientRect();
  if (!card) return viewportFrame();
  return { left: card.left, top: card.top, width: card.width, height: card.height };
};

/** Travel available to the icon's top-left corner on one axis of the frame. */
const axisBounds = (start: number, size: number): { min: number; max: number } => {
  const min = start + ACCESSIBILITY_EDGE_MARGIN_PX;
  const max = Math.max(min, start + size - ACCESSIBILITY_ICON_SIZE_PX - ACCESSIBILITY_EDGE_MARGIN_PX);
  return { min, max };
};

export const ratioToPx = (ratio: AccessibilityPositionRatio, frame: IconFrame): { left: number; top: number } => {
  const x = axisBounds(frame.left, frame.width);
  const y = axisBounds(frame.top, frame.height);
  return {
    left: x.min + clampRatio(ratio.x) * (x.max - x.min),
    top: y.min + clampRatio(ratio.y) * (y.max - y.min),
  };
};

export const pxToRatio = (position: { left: number; top: number }, frame: IconFrame): AccessibilityPositionRatio => {
  const x = axisBounds(frame.left, frame.width);
  const y = axisBounds(frame.top, frame.height);
  const xTravel = x.max - x.min;
  const yTravel = y.max - y.min;
  return {
    x: xTravel > 0 ? clampRatio((position.left - x.min) / xTravel) : 0,
    y: yTravel > 0 ? clampRatio((position.top - y.min) / yTravel) : 0,
  };
};

/**
 * Where the icon starts before anyone drags it: the card's bottom-left
 * corner, inset by the amount the Sprint 94 Figma specifies for this
 * breakpoint — 12/12 on the 375x812 mobile frame, 37/27 on the 976x871
 * desktop one, where 37 is the card's own content gutter.
 */
export const resolveDefaultPositionRatio = (frame: IconFrame = currentFrame()): AccessibilityPositionRatio => {
  const inset = isMobileDevice() ? ACCESSIBILITY_FRAME_INSET_MOBILE : ACCESSIBILITY_FRAME_INSET_DESKTOP;
  return pxToRatio(
    {
      left: frame.left + inset.left,
      top: frame.top + frame.height - inset.bottom - ACCESSIBILITY_ICON_SIZE_PX,
    },
    frame
  );
};

/**
 * EqualWeb renders the button either straight into the document or inside a
 * shadow host, depending on the publisher's script build — so both are checked.
 */
export const findAccessibilityButton = (root: ParentNode = document): HTMLElement | null => {
  const direct = root.querySelector<HTMLElement>(`#${ACCESSIBILITY_BUTTON_ID}`);
  if (direct) return direct;
  const host = root.querySelector<HTMLElement>(`#${ACCESSIBILITY_SHADOW_HOST_ID}`);
  return host?.shadowRoot?.querySelector<HTMLElement>(`#${ACCESSIBILITY_BUTTON_ID}`) ?? null;
};

/**
 * Pins the button to `ratio`. Every declaration is `!important` because
 * EqualWeb writes its own inline styles on the same element and wins ties.
 */
export const applyAccessibilityButtonPosition = (
  button: HTMLElement,
  ratio: AccessibilityPositionRatio,
  frame: IconFrame = currentFrame()
): void => {
  const { left, top } = ratioToPx(ratio, frame);
  button.style.setProperty('position', 'fixed', 'important');
  button.style.setProperty('left', `${left}px`, 'important');
  button.style.setProperty('top', `${top}px`, 'important');
  button.style.setProperty('right', 'auto', 'important');
  button.style.setProperty('bottom', 'auto', 'important');
  button.style.setProperty('margin', '0', 'important');
  // EqualWeb scales from the top left, which is also the corner we position
  // by — so the painted box lands exactly on `left`/`top`.
  button.style.setProperty('transform-origin', 'top left', 'important');
  button.style.setProperty('--indscale', String(ACCESSIBILITY_ICON_SIZE_PX / ACCESSIBILITY_BUTTON_NATIVE_SIZE_PX));
  button.style.setProperty('z-index', String(ACCESSIBILITY_ICON_Z_INDEX), 'important');
  // Without this a touch-drag is claimed by the page scroller instead.
  button.style.setProperty('touch-action', 'none', 'important');
};

interface DraggableOptions {
  /** Read at `pointerdown`, so a resize between gestures is picked up. */
  getPosition: () => AccessibilityPositionRatio;
  onDragStart: () => void;
  onDragEnd: (ratio: AccessibilityPositionRatio) => void;
}

/**
 * Makes EqualWeb's button draggable without taking its click away: the pointer
 * stream is observed, not consumed, so anything under
 * {@link ACCESSIBILITY_DRAG_THRESHOLD_PX} reaches the widget's own handler and
 * opens the menu. Past the threshold the gesture becomes a drag and the click
 * that the browser synthesizes on release is swallowed in the capture phase,
 * so dropping the icon never opens the menu.
 */
export const makeAccessibilityButtonDraggable = (button: HTMLElement, options: DraggableOptions): (() => void) => {
  let activePointerId: number | null = null;
  let startClientX = 0;
  let startClientY = 0;
  let startLeft = 0;
  let startTop = 0;
  let latestRatio: AccessibilityPositionRatio | null = null;
  let hasMoved = false;
  let swallowClickTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const disarmSwallowClick = () => {
    if (swallowClickTimeoutId !== null) {
      clearTimeout(swallowClickTimeoutId);
      swallowClickTimeoutId = null;
    }
    window.removeEventListener('click', swallowClick, { capture: true });
  };

  const swallowClick = (event: MouseEvent) => {
    // `once` already drops the listener; clear the fallback timer so it cannot
    // remove a later arm from a subsequent drag.
    if (swallowClickTimeoutId !== null) {
      clearTimeout(swallowClickTimeoutId);
      swallowClickTimeoutId = null;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const armSwallowClick = () => {
    disarmSwallowClick();
    window.addEventListener('click', swallowClick, { capture: true, once: true });
    swallowClickTimeoutId = setTimeout(() => {
      swallowClickTimeoutId = null;
      window.removeEventListener('click', swallowClick, { capture: true });
    }, 0);
  };

  const endGesture = (expectSyntheticClick: boolean) => {
    activePointerId = null;
    if (!hasMoved) return;
    hasMoved = false;
    // Capture phase, so it runs before EqualWeb's own click handler.
    if (expectSyntheticClick) armSwallowClick();
    if (latestRatio) options.onDragEnd(latestRatio);
  };

  const onPointerDown = (event: PointerEvent) => {
    if (activePointerId !== null || event.isPrimary === false) return;
    activePointerId = event.pointerId;
    hasMoved = false;
    startClientX = event.clientX;
    startClientY = event.clientY;
    // Derived from the position we last wrote rather than read back off the
    // element: EqualWeb scales the button with a transform, and its own style
    // rewrites can land between two gestures.
    const start = ratioToPx(options.getPosition(), currentFrame());
    startLeft = start.left;
    startTop = start.top;
    latestRatio = null;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;
    const deltaX = event.clientX - startClientX;
    const deltaY = event.clientY - startClientY;
    if (!hasMoved && Math.hypot(deltaX, deltaY) < ACCESSIBILITY_DRAG_THRESHOLD_PX) return;

    if (!hasMoved) {
      hasMoved = true;
      options.onDragStart();
      // Only now, once this is certainly a drag, is the pointer claimed — a
      // tap has to keep its default sequence for the widget to open.
      button.setPointerCapture?.(event.pointerId);
    }
    if (event.cancelable) event.preventDefault();
    // Clamped to the card, not the window: the design anchors the icon to the
    // checkout frame, so a drag must not be able to park it out on the scrim.
    const frame = currentFrame();
    latestRatio = pxToRatio({ left: startLeft + deltaX, top: startTop + deltaY }, frame);
    applyAccessibilityButtonPosition(button, latestRatio, frame);
  };

  const onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;
    endGesture(true);
  };

  const onPointerCancel = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;
    // No compat click follows a cancel — arming a swallow would eat the next tap.
    endGesture(false);
  };

  button.addEventListener('pointerdown', onPointerDown);
  // On window, so a drag that outruns the pointer still tracks and a pointer
  // cancelled outside the button (scroll takeover, tab switch) still settles.
  window.addEventListener('pointermove', onPointerMove, { passive: false });
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerCancel);

  return () => {
    button.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    disarmSwallowClick();
  };
};

/**
 * Waits for EqualWeb to paint its button — it is injected asynchronously and
 * re-styled afterwards — then keeps it at the shopper's position and makes it
 * draggable. Returns a cleanup that detaches every observer and listener.
 */
export const watchAccessibilityWidget = (): (() => void) => {
  // The drag lives for as long as the checkout is open and is deliberately not
  // persisted. While it is `null` the position is re-derived from the checkout
  // card on every placement, so the icon tracks the card's own layout.
  let draggedPosition: AccessibilityPositionRatio | null = null;
  let isDragging = false;
  let boundButton: HTMLElement | null = null;
  let observedCard: HTMLElement | null = null;
  let detachDrag: (() => void) | undefined;
  const shadowObservers = new Map<HTMLElement, MutationObserver>();

  const currentPosition = (frame: IconFrame) => draggedPosition ?? resolveDefaultPositionRatio(frame);

  const place = (button: HTMLElement) => {
    if (isDragging) return;
    const frame = currentFrame();
    const ratio = currentPosition(frame);
    const { left, top } = ratioToPx(ratio, frame);
    // The document observer fires on nearly every checkout render. Comparing
    // against the element's own inline style — rather than a remembered value
    // — skips those redundant writes while still re-applying the moment
    // EqualWeb overwrites our declarations with its own.
    if (
      button.style.getPropertyValue('position') === 'fixed' &&
      button.style.getPropertyValue('left') === `${left}px` &&
      button.style.getPropertyValue('top') === `${top}px`
    ) {
      return;
    }
    applyAccessibilityButtonPosition(button, ratio, frame);
  };

  const bind = (button: HTMLElement) => {
    if (boundButton === button) {
      place(button);
      return;
    }
    detachDrag?.();
    boundButton = button;
    place(button);
    detachDrag = makeAccessibilityButtonDraggable(button, {
      getPosition: () => currentPosition(currentFrame()),
      onDragStart: () => {
        isDragging = true;
      },
      onDragEnd: (ratio) => {
        isDragging = false;
        draggedPosition = ratio;
      },
    });
  };

  const observeShadowHost = (host: HTMLElement) => {
    if (!host.shadowRoot || shadowObservers.has(host)) return;
    const observer = new MutationObserver(() => {
      sync();
      // Drop the records our own style writes just queued, so re-applying
      // the position cannot feed itself.
      observer.takeRecords();
    });
    observer.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
    shadowObservers.set(host, observer);
  };

  /** The card moves and resizes under the icon, so re-place on every change. */
  const onFrameChange = () => {
    if (boundButton) place(boundButton);
  };

  // A `resize` event is not guaranteed for every change of the layout
  // viewport, so the root element's own box is watched as well — otherwise an
  // icon parked at the right edge of a wide viewport can end up off-screen
  // after the viewport narrows (reproduced under device emulation, which
  // resizes the layout viewport without firing `resize`).
  const boxObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onFrameChange) : undefined;

  /**
   * Follows the checkout card, which mounts after the boot skeleton and is
   * swapped between the modal and landscape layouts. Positions are measured
   * against it, so its box has to be watched directly — `Modal` resizes it
   * (`90vw/90vh` capped at `976x871`) without the root element changing.
   */
  const trackFrame = () => {
    const card = findAccessibilityFrameElement();
    if (card === observedCard) return;
    if (observedCard) boxObserver?.unobserve(observedCard);
    observedCard = card;
    if (card) boxObserver?.observe(card);
    onFrameChange();
  };

  const sync = () => {
    const button = findAccessibilityButton();
    if (button) bind(button);
    const host = document.getElementById(ACCESSIBILITY_SHADOW_HOST_ID);
    if (host) observeShadowHost(host);
    trackFrame();
  };

  boxObserver?.observe(document.documentElement);
  sync();
  // `childList` only: EqualWeb's own style rewrites on the button arrive
  // through the shadow observer above, and watching attributes here would
  // re-enter on every style we write ourselves.
  const documentObserver = new MutationObserver(sync);
  documentObserver.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', onFrameChange);
  window.addEventListener('orientationchange', onFrameChange);

  return () => {
    documentObserver.disconnect();
    shadowObservers.forEach((observer) => observer.disconnect());
    shadowObservers.clear();
    boxObserver?.disconnect();
    observedCard = null;
    detachDrag?.();
    detachDrag = undefined;
    boundButton = null;
    window.removeEventListener('resize', onFrameChange);
    window.removeEventListener('orientationchange', onFrameChange);
  };
};
